import { Inject, Injectable, OnDestroy, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { environment } from '@environments/environment';
import { Subject, filter, takeUntil } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class GtagService implements OnDestroy {

  private readonly DEFAULT_GTAG_ID = 'G-KC2KVPR57N';
  private readonly destroy$ = new Subject<void>();

  private isBrowser: boolean;
  private gtagIds: string[] = [];

  private started = false;
  private loaded = false;
  private loadedGtm = false;

  private readonly LOAD_FALLBACK_MS = 10000;
  private readonly GTM_ID_PREFIX = 'GTM-';
  private readonly GA4_ID_PREFIX = 'G-';

  constructor(
    @Inject(PLATFORM_ID) platformId: Object,
    private router: Router
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.gtagIds = this.resolveGtagIds();
  }

  start(): void {
    if (!this.isBrowser || this.started) {
      return;
    }

    this.started = true;

    this.attachIdleLoadListener();
    this.attachRouterListener();
    this.scheduleLoadFallback();
  }

  /**
   * Send an event to GTM and/or GA4 with the given name and optional parameters.
   * Call this whenever the user does something you want to track (clicks, form submit, etc.).
   */
  sendEvent(eventName: string, params?: Record<string, unknown>): void {
    if (!this.isBrowser || !this.loaded) {
      return;
    }

    const payload = {
      page_path: (params?.page_path as string) || (typeof window !== 'undefined' ? window.location?.pathname : ''),
      page_location: typeof window !== 'undefined' ? window.location?.href : '',
      page_title: typeof document !== 'undefined' ? document?.title : '',
      ...params
    };

    if (this.loadedGtm) {
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push({ event: eventName, ...payload });
    }

    if ((window as any).gtag) {
      (window as any).gtag('event', eventName, payload);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private resolveGtagIds(): string[] {
    const ids: string[] = [];
    const seen = new Set<string>();

    const add = (id: string) => {
      const normalized = (id || '').trim();

      if (normalized && !seen.has(normalized)) {
        seen.add(normalized);
        ids.push(normalized);
      }
    };

    const whiteLabelConfig = (environment as any)?.whiteLabelConfig;

    const isWhiteLabel =
      whiteLabelConfig &&
      typeof whiteLabelConfig === 'object' &&
      Object.keys(whiteLabelConfig).length > 0;

    if (isWhiteLabel) {

      (environment.whitelableGtag || '')
        .split(',')
        .forEach(add);

      const dynamicId =
        whiteLabelConfig?.data?.whiteLabel?.gTagId ||
        whiteLabelConfig?.data?.whiteLabel?.gtag_id;

      if (dynamicId) {
        add(dynamicId);
      }
    }

    add(this.DEFAULT_GTAG_ID);

    return ids;
  }

  private maybeLoad(): void {
    if (this.loaded || !this.gtagIds.length) {
      return;
    }

    this.loadGtag();
  }

  private isGtmId(id: string): boolean {
    return (id || '').trim().startsWith(this.GTM_ID_PREFIX);
  }

  private isGa4Id(id: string): boolean {
    return (id || '').trim().startsWith(this.GA4_ID_PREFIX);
  }

  private loadGtag(): void {

    if (!this.isBrowser || this.loaded) {
      return;
    }
    this.loaded = true;

    const w = window as any;
    w.dataLayer = w.dataLayer || [];

    this.ensureGtagFunction();
    this.setDefaultConsent();

    const gtmIds = this.gtagIds.filter(id => this.isGtmId(id));
    const ga4Ids = this.gtagIds.filter(id => this.isGa4Id(id));

    if (gtmIds.length > 0) {
      gtmIds.forEach(gtmId => this.loadGtmScript(gtmId));
      this.loadedGtm = true;
    }

    if (ga4Ids.length > 0) {
      this.setupGa4AndLoadScript(ga4Ids);
    }

    this.sendInitialPageView();

    if (!environment.production && this.gtagIds.length > 0) {
      console.log(`Gtag loaded with ${this.gtagIds.length} ID(s) configured (GTM: ${gtmIds.length}, GA4: ${ga4Ids.length})`);
    }
  }

  private loadGtmScript(gtmId: string): void {

    if (document.querySelector(`script[data-gtm-id="${gtmId}"]`)) {
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.setAttribute('data-gtm-id', gtmId);
    script.src =
      'https://www.googletagmanager.com/gtm.js?id=' +
      encodeURIComponent(gtmId);

    document.head.appendChild(script);
  }

  private ensureGtagFunction(): void {

    const w = window as any;

    if (typeof w.gtag === 'function') {
      return;
    }

    const gtag = function (..._args: unknown[]) {
      w.dataLayer.push(arguments);
    };

    w.gtag = gtag;
    (globalThis as any).gtag = gtag;
  }

  /**
   * Consent Mode v2 withholds all network hits until storage is granted, so this
   * must be queued before gtm.js/gtag.js execute.
   */
  private setDefaultConsent(): void {

    (window as any).gtag('consent', 'default', {
      analytics_storage: 'granted',
      ad_storage: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'granted',
      functionality_storage: 'granted',
      personalization_storage: 'granted',
      security_storage: 'granted'
    });
  }

  private setupGa4AndLoadScript(ga4Ids: string[]): void {

    const gtag = (window as any).gtag;

    gtag('js', new Date());

    ga4Ids.forEach(id => {
      gtag('config', id, {
        send_page_view: false
      });
    });

    if (document.querySelector('script[data-gtag]')) {
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.setAttribute('data-gtag', 'true');
    script.src =
      'https://www.googletagmanager.com/gtag/js?id=' +
      encodeURIComponent(ga4Ids[0]);

    document.head.appendChild(script);
  }

  private sendInitialPageView(): void {
    const payload = {
      page_path: window.location.pathname,
      page_location: window.location.href,
      page_title: document.title
    };

    if (this.loadedGtm) {
      (window as any).dataLayer.push({ event: 'page_view', ...payload });
    }

    if ((window as any).gtag) {
      (window as any).gtag('event', 'page_view', payload);
    }

    if (!environment.production) {
      console.log('[GtagService] Initial page_view sent', payload);
    }
  }

  private attachIdleLoadListener(): void {

    if (!this.isBrowser) {
      return;
    }

    const schedule = () => {

      if ('requestIdleCallback' in window) {

        (window as any).requestIdleCallback(() => this.maybeLoad(), { timeout: 3000 });

      } else {

        setTimeout(() => this.maybeLoad(), 3000);

      }
    };

    if (document.readyState === 'complete') {
      schedule();
    } else {
      window.addEventListener('load', schedule);
    }
  }

  private scheduleLoadFallback(): void {

    if (!this.isBrowser) {
      return;
    }

    setTimeout(() => this.maybeLoad(), this.LOAD_FALLBACK_MS);
  }

  private attachRouterListener(): void {

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {

        if (!this.loaded) {
          return;
        }

        const path = event.urlAfterRedirects;
        const payload = {
          page_path: path,
          page_location: window.location.href,
          page_title: document.title
        };

        if (this.loadedGtm) {
          (window as any).dataLayer = (window as any).dataLayer || [];
          (window as any).dataLayer.push({ event: 'page_view', ...payload });
        }

        if ((window as any).gtag) {
          (window as any).gtag('event', 'page_view', payload);
        }

      });
  }

}